<template>
  <m-dropdown
    v-model:value="expanded"
    class="ai-chat-history-dropdown"
    :title="$t('aiChat.history')"
    placement="bottomLeft"
  >
    <m-btn
      class="_trigger"
      hide-border
      small
    >
      <span class="_label">{{ label }}</span>
      <m-icon
        type="down"
        size="11"
        :color="$colors.grey.lighten1"
      />
    </m-btn>
    <template #overlay>
      <m-card
        no-padding
        :class="['_overlay', smAndDown ? '-mobile' : '']"
      >
        <div class="_content">
          <m-content
            :padding-x="7"
            :padding-top="7"
            :padding-bottom="5"
          >
            <m-text-field
              v-model:value="search"
              auto-focus
              has-background
              :placeholder="$t('aiChat.searchPlaceholder')"
            >
              <template #suffix>
                <m-icon
                  v-if="searching"
                  type="loading"
                  spin
                  :color="$colors.grey.lighten1"
                />
              </template>
            </m-text-field>
          </m-content>
          <div class="_body">
            <m-endless-scroll-list @visible="loadMore">
              <template
                v-for="(group, index) in groups"
                :key="group.key"
              >
                <m-divider
                  v-if="index > 0"
                  xxs
                />
                <sub-heading :title="$t(`aiChat.groups.${group.key}`)" />
                <m-card-item
                  v-for="chat in group.chats"
                  :key="chat.uid"
                  @mouseover="hoveredChatId = chat.uid"
                  @mouseleave="hoveredChatId = null"
                  @click="select(chat)"
                >
                  <span class="_title">{{ title(chat) }}</span>
                  <template
                    v-if="showsMenu(chat) || isSelected(chat)"
                    #right
                  >
                    <div class="_actions">
                      <m-icon
                        v-if="isSelected(chat)"
                        type="check"
                        :color="$colors.grey.darken4"
                        size="14"
                      />
                      <ai-chat-chat-menu
                        v-if="showsMenu(chat)"
                        xs
                        :chat-id="chat.uid"
                        :expanded="menuChatId === chat.uid"
                        @update:expanded="setMenuChat(chat, $event)"
                      />
                    </div>
                  </template>
                </m-card-item>
              </template>
              <div
                v-if="loadingMore"
                class="_loading-more"
              >
                <m-spinner size="xs" />
              </div>
            </m-endless-scroll-list>
            <m-card-item
              v-if="groups.length === 0 && !searching"
              :clickable="false"
              no-hover
              light
            >
              {{ statusMessage }}
            </m-card-item>
          </div>
        </div>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import AiChatChatMenu from '@/components/ai-chat/AiChatChatMenu.vue';
import SubHeading from 'shared/components/SubHeading.vue';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import useDebounce from 'shared/composables/debounce';
import useSnackBar from '@/composables/snackbar';
import { chatTitle, groupChatsByRecency } from '@/lib/ai-chat-panel';
import { computed, ref, watch } from 'vue';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const ITEMS_PER_PAGE = 25;
const SEARCH_DEBOUNCE = 500;

const props = defineProps({
  chatId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const store = useStore();
const snackbar = useSnackBar();
const { debounce, cancelAll } = useDebounce({ onBeforeUnmountCancel: true });
const { chats, getListLoading, queryChatList } = useAiChatRepo();

const expanded = ref(false);
const search = ref('');
const page = ref(1);
const exhausted = ref(false);
const fetchingPage = ref(1);
const loadedTerm = ref(null);
const hoveredChatId = ref(null);
const menuChatId = ref(null);

const title = (chat) => chatTitle(chat, t('aiChat.untitled'));

const searchTerm = computed(() => search.value.trim());

// the server filter decides which chats get fetched, but nebula keeps every chat ever
// loaded, so the term has to narrow the store again before the list is rendered
const matches = computed(() => {
  const term = searchTerm.value.toLowerCase();
  if (term === '') {
    return chats.value;
  }
  return chats.value.filter((chat) => title(chat).toLowerCase().includes(term));
});

const groups = computed(() => groupChatsByRecency(matches.value));

const statusMessage = computed(() => (searchTerm.value === ''
  ? t('aiChat.noChats')
  : t('aiChat.noResults')));

const label = computed(() => {
  if (props.chatId === null) {
    return t('aiChat.newChatTitle');
  }
  return title(chats.value.find((chat) => chat.uid === props.chatId) ?? {});
});

const smAndDown = computed(() => store.state.breakpoint.smAndDown);

const isSelected = (chat) => chat.uid === props.chatId;

// mobile has no hover to reveal the menu on
const showsMenu = (chat) => smAndDown.value
  || hoveredChatId.value === chat.uid
  || menuChatId.value === chat.uid;

const setMenuChat = (chat, open) => {
  menuChatId.value = open ? chat.uid : null;
};

const select = (chat) => {
  expanded.value = false;
  emit('select', chat.uid);
};

const searching = computed(() => getListLoading.value && fetchingPage.value === 1);
const loadingMore = computed(() => getListLoading.value && fetchingPage.value > 1);

const fetchPage = (nextPage) => {
  fetchingPage.value = nextPage;
  return queryChatList({
    pagination: { page: nextPage, itemsPerPage: ITEMS_PER_PAGE },
    searchTerm: searchTerm.value,
  }).then(({ count }) => {
    page.value = nextPage;
    loadedTerm.value = searchTerm.value;
    exhausted.value = matches.value.length >= count;
  });
};

const restart = () => {
  exhausted.value = false;
  fetchPage(1).catch(logCatch(() => { snackbar.error(); }));
};

const loadMore = () => {
  if (getListLoading.value || exhausted.value) {
    return;
  }
  fetchPage(page.value + 1).catch(logCatch(() => { snackbar.error(); }));
};

watch(expanded, (shown) => {
  if (!shown) {
    cancelAll();
    search.value = '';
    hoveredChatId.value = null;
    menuChatId.value = null;
    return;
  }
  // the first page is already fetched when the panel opens, so opening the picker
  // only queries again when a previous search left the pagination on a filtered list
  if (loadedTerm.value !== searchTerm.value) {
    restart();
  }
});

watch(search, () => {
  if (!expanded.value) {
    return;
  }
  debounce(restart, SEARCH_DEBOUNCE);
});

restart();
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-history-dropdown {
    min-width: 0;

    ._trigger {
      gap: .6rem;
      min-width: 0;
      max-width: 100%;
    }

    ._label {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      min-width: 0;
    }

    ._trigger .m-icon {
      flex-shrink: 0;
    }
  }

  ._overlay {
    width: 28rem;

    ._body {
      height: 32rem;
      max-height: 40vh;
      padding-bottom: .4rem;
      overflow-x: hidden;
      overflow-y: auto;
    }

    // the dropdown goes fullscreen on mobile, so the list has to fill what is left of the
    // card instead of ending mid screen
    &.-mobile {
      width: 100%;

      ._content {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      ._body {
        height: auto;
        max-height: none;
      }
    }

    ._title {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    ._actions {
      display: flex;
      gap: .4rem;
      align-items: center;
    }

    ._loading-more {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 4rem;
    }
  }
</style>
